import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { BrowserDetectionService } from '@llm/service/browser_detection.service';
import { TextToSpeechService } from '@llm/service/llm_text_speench.service';
import { SpeechSynthesizerService } from '@llm/service/speech_synthesizer.service'; // Este é o serviço que usa a API nativa do navegador
import {llmStoreService} from '@llm/service/llm_store.service';
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";
import { marked } from 'marked';
import {Utilities} from '@app/class/utilities';

/**
 * Interface para os itens na fila de áudio.
 * Inclui as funções resolve e reject da Promise associada a cada mensagem,
 * permitindo que a Promise retorne apenas quando a reprodução da mensagem estiver concluída.
 */
interface AudioQueueItem {
  message: { id: string; content: string };
  print: boolean;
  resolve: () => void;
  reject: (error: any) => void;
}

@Injectable({ providedIn: 'root' })
export class PlayMessageService { // O nome do serviço está correto aqui
  // Injeções de Dependência
  store = inject(llmStoreService);
  browserDetector = inject(BrowserDetectionService);
  ttsService = inject(TextToSpeechService); // Serviço para síntese de fala via API (ex: LLM)
  speechSynthesizer = inject(SpeechSynthesizerService); // Serviço para síntese de fala nativa do navegador
  private _console = inject(Tools_aiConsoleService);
  // Fila de áudio para gerenciar a reprodução sequencial
  private audioQueue: AudioQueueItem[] = [];
  // `isProcessing` é uma variável de controlo interna para saber se a fila está ativa
  private isProcessing = false;

  // Subject para emitir mensagens que devem ser impressas na UI
  public onMessagePrint = new Subject<{ id: string; content: string }>();

  constructor() {
    // Não é necessária inicialização explícita aqui, pois as injeções são diretas e a lógica
    // de inicialização da fila é disparada por `playMessage`.
  }

  /**
   * Enfileira uma mensagem para reprodução e retorna uma Promise que
   * é resolvida quando a mensagem específica termina de ser reproduzida.
   *
   * @param message O texto a ser reproduzido.
   * @param print Indica se a mensagem deve ser impressa (emitida via onMessagePrint).
   * @returns Uma Promise que resolve quando a reprodução desta mensagem é concluída.
   */
  async playMessage(message: { id: string; content: string } | string, print: boolean = false): Promise<void> {
    this._console.log('PlayMessageService: Adicionando mensagem à fila.'); 
    return new Promise<void>((resolve, reject) => {
        if(typeof message === "string"){
            message = {
                content: message,
                id: `sys-msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
            }
            this.audioQueue.push({ message, print, resolve, reject });
        }else{
            this.audioQueue.push({ message, print, resolve, reject });
        }


      // Se a fila não estiver a ser processada, inicie-a.
      // O store.isSpeaking deve refletir o estado geral de "algum áudio a tocar",
      // enquanto isProcessing é para o controlo interno da fila.
      if (!this.isProcessing) {
        this.isProcessing = true; // Define que o processamento da fila está ativo
        this.store.isSpeaking.set(true); // Informa ao store que o sistema está a falar
        this.processQueue();
      }
    });
  }

  /**
   * Processa a fila de áudio de forma recursiva.
   * Reproduz a próxima mensagem na fila e, quando termina, chama-se recursivamente
   * para processar a próxima, até que a fila esteja vazia.
   * Resolve ou rejeita a Promise da mensagem atual quando ela termina ou falha.
   */
  private async processQueue(): Promise<void> {
    // Se a fila estiver vazia, para o processamento.
    if (this.audioQueue.length === 0) {
      this._console.log('PlayMessageService: Fila de áudio vazia. Definindo estados para falso.');
      this.isProcessing = false;
      this.store.isSpeaking.set(false);
      return;
    }

    const next = this.audioQueue.shift()!;

    if (next.print) {
      this.onMessagePrint.next(next.message);
    }
    next.message.content = this.clearTextPortugues(next.message.content).trim()

    try {
      // Decide qual método de reprodução usar com base no navegador.
        //await this.handleStandardPlayback(next.message);
      /*if (this.browserDetector.isEdge()) {
        await this.handleEdgePlayback(next.message);
      } else {
        await this.handleStandardPlayback(next.message);
      }*/
      next.resolve();
      this._console.log('PlayMessageService: Mensagem reproduzida com sucesso, resolvendo Promise.');
    } catch (error) {
      // Em caso de erro na reprodução, rejeita a Promise associada à mensagem atual.
      this._console.error("PlayMessageService: Erro ao reproduzir áudio:", error); 
      next.reject(error);
    }

    // Chama recursivamente para processar o próximo item na fila.
    // Esta chamada é feita fora do bloco `try-catch` para garantir que a fila continue
    // a ser processada mesmo que uma mensagem falhe.
    this.processQueue();
  }

  /**
   * Lida com a reprodução de áudio especificamente para o navegador Edge,
   * utilizando o serviço de síntese de fala nativa do navegador.
   *
   * @param message O texto a ser reproduzido.
   * @returns Uma Promise que resolve quando a fala é concluída.
   */
  private async handleEdgePlayback(message: string): Promise<void> {
    // Assumimos que this.speechSynthesizer.speak já retorna uma Promise que resolve
    // quando a reprodução do áudio nativo do navegador termina.
    await this.speechSynthesizer.speak(message);
    this._console.log('PlayMessageService: Reprodução para Edge concluída.');
  }

  /**
   * Lida com a reprodução de áudio para navegadores padrão,
   * utilizando um serviço de Text-to-Speech (TTS) externo/API para obter dados de áudio.
   * Em caso de falha, utiliza o método de fallback para Edge.
   *
   * @param message O texto a ser reproduzido.
   * @returns Uma Promise que resolve quando a fala é concluída.
   */
  private async handleStandardPlayback(message: string): Promise<void> {
    try {
      // Sintetiza a fala através do serviço TTS externo e obtém os dados de áudio.
      const audioData = await this.ttsService.synthesizeSpeech(message).toPromise();
      // Reproduz o buffer de áudio obtido.
      await this.playAudioBuffer(audioData, message);
      this._console.log('PlayMessageService: Reprodução padrão concluída.');
    } catch (error) {
      // Em caso de falha na síntese ou reprodução padrão, recorre ao método para Edge.
      this._console.error('PlayMessageService: Falha na reprodução TTS primária, usando fallback (Edge):', error); 
      await this.handleEdgePlayback(message);
    }
  }

  /**
   * Reproduz um buffer de áudio obtido de um serviço TTS.
   * Cria um objeto Audio e resolve a Promise quando o áudio termina.
   * Em caso de falha na reprodução do Audio, usa a síntese de fala nativa como fallback.
   *
   * @param audioData O ArrayBuffer contendo os dados do áudio.
   * @param message O texto original da mensagem (opcional, para fallback).
   * @returns Uma Promise que resolve quando o áudio termina de tocar.
   */
  private async playAudioBuffer(audioData: ArrayBuffer, message: string = undefined): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!audioData) {
        this._console.warn('PlayMessageService: AudioData é nulo ou indefinido para playAudioBuffer.');
        reject(new Error('AudioData é nulo ou indefinido.'));
        return;
      }

      const blob = new Blob([audioData], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);

      audio.onended = () => {
        URL.revokeObjectURL(url); // Libera o URL do objeto após o uso.
        resolve(); // Resolve a Promise quando o áudio termina.
      };

      // Adiciona um handler de erro para o objeto Audio
      audio.onerror = (e) => {
        URL.revokeObjectURL(url); // Libera o URL mesmo em caso de erro.
        this._console.error('PlayMessageService: Erro durante a reprodução de áudio:', e);
        // Tenta usar o fallback de síntese de fala nativa se houver uma mensagem.
        if (message) {
          this.speechSynthesizer.speak(message)
            .then(resolve) // Resolve a Promise original quando o fallback terminar
            .catch(fallbackError => {
              this._console.error('PlayMessageService: Falha no fallback de síntese de fala nativa:', fallbackError);
              reject(fallbackError); // Rejeita a Promise original se o fallback também falhar
            });
        } else {
          reject(new Error('Falha na reprodução de áudio e sem mensagem para fallback.')); // Rejeita se não houver fallback
        }
      };

      audio.play().catch(e => {
        // Este catch lida com erros na própria chamada `audio.play()`
        this._console.error('PlayMessageService: Falha ao iniciar a reprodução de áudio:', e);
        // Usa o fallback para síntese de fala nativa
        if (message) {
          this.speechSynthesizer.speak(message)
            .then(resolve) // Resolve a Promise original quando o fallback terminar
            .catch(fallbackError => {
              this._console.error('PlayMessageService: Falha no fallback de síntese de fala nativa:', fallbackError);
              reject(fallbackError); // Rejeita a Promise original se o fallback também falhar
            });
        } else {
          reject(new Error('Falha ao iniciar reprodução de áudio e sem mensagem para fallback.'));
        }
      });
    });
  }

  /**
   * REMOVIDO: Este método não é mais necessário.
   * Assumimos que `this.speechSynthesizer.speak(message)` e `this.playAudioBuffer`
   * já retornam Promises que resolvem *após a conclusão real* da reprodução do áudio.
   * Um atraso artificial não é preciso e pode causar inconsistências.
   */
  // private async delayForSpeech(message: string): Promise<void> {
  //   const words = message.split(/\s+/).length;
  //   const duration = Math.max(1500, (words / 3) * 1000); // 3 palabras/segundo
  //   return new Promise(resolve => setTimeout(resolve, duration));
  // }

    /**
     * Limpia un texto eliminando caracteres especiales,
     * pero mantiene los caracteres propios del portugués brasileño.
     * @param texto Texto de entrada
     * @returns Texto limpio
     */
    private clearTextPortugues(texto: string): string {
        if (!texto) return '';
        return texto
            .normalize('NFC') // Mantiene la forma compuesta de caracteres acentuados
            .replace(/[^a-zA-Z0-9\sáàâãäéèêëíìîïóòôõöúùûüçÁÀÂÃÄÉÈÊËÍÌÎÏÓÒÔÕÖÚÙÛÜÇ]/g, '');
    }
}
